import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import MissionDebugContext, {
  MissionDebugContextValue,
} from '../../context/MissionDebugContext';
import missionDebugRegistry from '../../services/missionDebugRegistry';
import { MissionDebugItem, MissionDebugMap } from '../../types/missionDebug';

interface MissionDebugProviderProps {
  children: React.ReactNode;
}

const SHOULD_LOG = process.env.NODE_ENV !== 'test';

/**
 * Provides the context for mission-specific debug state registration.
 * Should wrap the primary content of a mission page.
 * Collects state/actions registered via hooks (useMissionDebugState, etc.)
 * and registers the collected map with the global missionDebugRegistry.
 */
const MissionDebugProvider: React.FC<MissionDebugProviderProps> = ({ children }) => {
  // Use a ref to hold the map. This map is specific to this provider instance.
  const localDebugMapRef = useRef<MissionDebugMap>(new Map());

  // Stable function for hooks to register themselves with this provider instance.
  // useCallback ensures this function reference remains stable across renders.
  const registerItem = useCallback((key: string, item: MissionDebugItem) => {
    localDebugMapRef.current.set(key, item);
    // Note: Registration happens in the useEffect below, not here directly,
    // to ensure the map is fully populated before registering globally.
    if (SHOULD_LOG) {
      console.log(`[MissionDebugProvider] Item added/updated locally: ${key}`);
    }
  }, []);

  // Effect for registering/unregistering with the global registry
  useEffect(() => {
    // Register this provider's complete map when the component mounts
    const currentMap = localDebugMapRef.current;
    missionDebugRegistry.register(currentMap);
    if (SHOULD_LOG) {
      console.log('[MissionDebugProvider] Registered local map with global registry.');
    }

    // Return a cleanup function to unregister when the component unmounts
    return () => {
      missionDebugRegistry.unregister(currentMap); // Pass the specific map instance
      if (SHOULD_LOG) {
        console.log('[MissionDebugProvider] Attempted unregister from global registry.');
      }
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const contextValue: MissionDebugContextValue = useMemo(() => ({
    registerItem,
  }), [registerItem]);

  return (
    <MissionDebugContext.Provider value={contextValue}>
      {children}
    </MissionDebugContext.Provider>
  );
};

export default MissionDebugProvider; 
